import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const stats = [
  { label: "Schools", value: "Every" },
  { label: "Churches", value: "All" },
  { label: "Backgrounds", value: "Any" },
  { label: "Cost", value: "$0" },
];

const AboutPage = () => {
  return (
    <>
      <PageHeader
        overline="Our Story"
        title="It started with"
        accent="one question."
        subtitle="What would it look like if high school students from across Richmond came together around Jesus?"
        testId="about-header"
        bgImage="https://images.unsplash.com/photo-1583586604855-62f0cec351d9?fm=jpg&q=85&w=2400&auto=format&fit=crop&ixlib=rb-4.1.0"
      />

      <section className="relative pb-24 md:pb-32" data-testid="about-story">
        <div className="glow-blue top-20 -right-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7 space-y-8">
            <p className="text-xl md:text-2xl leading-relaxed text-white/80">
              RVA Students started with a simple question: what would it look like if high
              school students from across Richmond came together around Jesus?
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-white/70">
              There are amazing youth groups, churches, and students all over our city, but we
              wanted to create a space where students from different schools, backgrounds, and
              churches could gather as <span className="text-forest-glow font-semibold">one</span>.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-white/70">
              This is not about one church, one person, or one program. It is about creating a
              place where students can encounter{" "}
              <span className="text-deepblue-glow font-semibold">Jesus</span>, build real
              friendships, ask honest questions, and grow in their faith.
            </p>
            <p className="font-heading font-bold text-2xl md:text-3xl leading-snug tracking-tight pt-4">
              We are just getting started, and we believe God has something{" "}
              <span className="text-forest-glow">special</span> for students in Richmond.
            </p>
          </div>

          <div className="lg:col-span-5 lg:pt-8">
            <div
              className="card-glass p-2 aspect-[4/5] relative overflow-hidden"
              data-testid="about-image-card"
            >
              <div
                className="w-full h-full rounded-2xl"
                style={{
                  backgroundImage:
                    "url('https://images.unsplash.com/photo-1628116904332-7c0ec0cbc623?fm=jpg&q=80&w=1400&auto=format&fit=crop&ixlib=rb-4.1.0')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent rounded-2xl" />
              <div className="absolute inset-0 rounded-2xl" style={{ background: "radial-gradient(circle at 30% 70%, rgba(0,255,102,0.10) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(59,130,246,0.14) 0%, transparent 55%)" }} />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="overline mb-2">Chapter One</p>
                <p className="font-heading font-black text-2xl uppercase leading-tight">
                  From <span className="text-forest-glow">RVA</span>,<br />
                  For <span className="text-deepblue-glow">RVA</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-24 border-y border-white/10" data-testid="about-stats">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={s.label} data-testid={`about-stat-${i}`}>
              <p className="font-heading font-black text-5xl md:text-6xl uppercase tracking-tight mb-2">
                <span className={i % 2 === 0 ? "text-forest-glow" : "text-deepblue-glow"}>
                  {s.value}
                </span>
              </p>
              <p className="text-xs uppercase tracking-[0.24em] text-white/50 font-heading">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="about-team">
        <div className="glow-blue top-1/2 -right-40 -translate-y-1/2 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="card-glass p-6 md:p-10 lg:p-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <p className="overline mb-6">A Note From The Team</p>
              <div
                className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10"
                data-testid="team-photo"
              >
                <img
                  src="/images/team.jpeg"
                  alt="The RVA Students team at a gathering"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight">
                    The Team
                  </p>
                  <p className="text-xs uppercase tracking-[0.24em] text-forest-glow font-heading mt-2">
                    RVA Students
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-6 lg:pt-6" data-testid="team-note">
              <p className="font-heading font-bold text-2xl md:text-3xl leading-snug tracking-tight">
                "We want to connect students in Richmond who love Jesus."
              </p>
              <p className="text-lg text-white/80 leading-relaxed">
                Hey friends!
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                We started RVA Students because we have a heart for connecting students
                all across Richmond. There are so many teenagers in this city who love
                Jesus — but most of us have no idea who each other are.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                We felt like God kept putting this on our hearts and showing us that
                Richmond needed something that brought students together. Not another
                church or another youth group, but a place where students from all over
                the city could worship Jesus together, build real friendships, and
                encourage each other in their faith.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                Our hope is that RVA Students becomes a place where everyone feels
                welcome. Whether you've been following Jesus your whole life or you're
                just curious about who He is — we'd love for you to come. We truly
                believe God is going to do something incredible through the students of
                Richmond, and we're excited to see what He has in store.
              </p>
              <p className="text-lg text-white/80 leading-relaxed pt-2">
                See you at a gathering soon.{" "}
                <span className="text-forest-glow">❤️</span>
              </p>
              <p className="text-lg text-white/60 leading-relaxed">
                — <span className="text-forest-glow font-semibold">The RVA Students Team</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 text-center" data-testid="about-cta">
        <div className="glow-green top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
        <div className="relative max-w-3xl mx-auto px-6">
          <p className="overline mb-6">Come Be Part Of It</p>
          <h3 className="font-heading font-black text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight mb-8">
            The Story Is Still<br /><span className="text-forest-glow">Being Written.</span>
          </h3>
          <Link to="/gatherings" className="btn-primary" data-testid="about-cta-button">
            Join The Next Gathering <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
